import { supabase } from '../lib/supabase';
import {
  ArgumentTemplate,
  Case,
  NotificationPreferences,
  SimulationSession,
  VerdictOutput,
  SimulationDebrief,
  PatternAnalysis,
  PersonaProfile,
  AccuracyStats,
  BestBackgroundResult,
  EvidenceAnalysis,
  EvidenceGapAnalysis,
  Witness,
  CaseDeadline,
  ArgumentVersion,
  TrialTimelineEvent,
  ChecklistTemplate,
  CaseDocument,
  ArgumentRepository,
  PostTrialFeedback,
  ContradictionReport,
  ContradictionPair,
  BillingStatus,
  CaseComment,
  CaseActivity,
  CaseShare,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

// Demo mode: when VITE_DEMO_MODE is true, the app returns rich sample data
// instead of hitting the backend. This lets every page render with populated
// data for demos, screenshots, and frontend previews.
import * as demo from '../data/demoData';

function isDemoMode(): boolean {
  if (import.meta.env.VITE_DEMO_MODE === 'true') return true;
  try {
    return sessionStorage.getItem('demo_mode') === 'true' || localStorage.getItem('demo_mode') === 'true';
  } catch {
    return false;
  }
}

function demoDelay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), 150));
}

// HTML escape helper to prevent XSS in exported reports
function escapeHtml(text: string | number | null | undefined): string {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  // Try session token first, then fall back to Supabase session
  let token = sessionStorage.getItem('token');
  if (!token) {
    const { data: { session } } = await supabase.auth.getSession();
    token = session?.access_token || null;
  }
  // Don't set Content-Type for FormData — the browser must set the
  // multipart boundary itself or the upload body can't be parsed.
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(`${API_BASE_URL}${url}`, { ...options, headers });
  // Redirect to login on 401 to avoid "stuck" loading states — but ONLY when we
  // actually sent a token. A signed-in Convex Auth user has no Express JWT and
  // no Supabase session, so `token` is null and every Express call 401s. This
  // used to wipe sessionStorage and hard-redirect to /login?expired=true, which
  // signed a perfectly valid user out of the app the moment a page touched a
  // legacy Express endpoint (Firm Settings and Firm Members both call
  // billingApi.getStatus() on mount). No token means "this Express endpoint is
  // not available on the Convex stack", not "your session expired" — surface it
  // to the caller instead of ejecting the user.
  if (res.status === 401 && !url.includes('/auth')) {
    if (!token) {
      return Promise.reject(
        new Error('This feature is not available on your account yet.')
      );
    }
    sessionStorage.removeItem('token');
    window.location.href = '/login?expired=true';
    // Return a rejected promise so callers know the request failed
    return Promise.reject(new Error('Session expired. Please sign in again.'));
  }
  return res;
}

// ==================== AUTH API ====================
export const authApi = {
  login: async (email: string, password: string) => {
    if (isDemoMode()) {
      return demoDelay({ user: demo.demoProfile, token: 'demo-token', firm: demo.demoFirm });
    }
    const res = await fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return res.json();
  },
  register: async (email: string, password: string, name: string, barNumber?: string, firmName?: string) => {
    if (isDemoMode()) {
      return demoDelay({ user: { ...demo.demoProfile, email, full_name: name }, token: 'demo-token', firm: demo.demoFirm });
    }
    const res = await fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, bar_number: barNumber, firm_name: firmName }),
    });
    return res.json();
  },
  me: async () => {
    if (isDemoMode()) return demoDelay({ user: demo.demoProfile, firm: demo.demoFirm });
    const res = await fetchWithAuth('/auth/me');
    return res.json();
  },
  cookie: async (_token: string) => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth('/auth/cookie', { method: 'POST', body: JSON.stringify({ token: _token }) });
    return res.json();
  },
};

// ==================== TEMPLATES API ====================

// Demo mode persists user-created templates in localStorage (mirroring the
// DEMO_SESSIONS_KEY pattern below) so "Save as Template" survives a reload
// and the saved template actually shows up in getAll/getById.
const DEMO_TEMPLATES_KEY = 'trialsim_demo_templates';

function getDemoCreatedTemplates(): ArgumentTemplate[] {
  try {
    const raw = localStorage.getItem(DEMO_TEMPLATES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveDemoCreatedTemplates(list: ArgumentTemplate[]) {
  try {
    localStorage.setItem(DEMO_TEMPLATES_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

export const templatesApi = {
  getAll: async (params?: {
    side?: string;
    practice_area?: string;
    framework_type?: string;
    search?: string;
    include_builtin?: boolean;
  }): Promise<{ templates: ArgumentTemplate[] }> => {
    if (isDemoMode()) {
      let list = [...getDemoCreatedTemplates(), ...demo.demoArgumentTemplates];
      if (params?.side) list = list.filter((t) => t.side === params.side);
      if (params?.practice_area) list = list.filter((t) => t.practice_area === params.practice_area);
      if (params?.framework_type) list = list.filter((t) => t.framework_type === params.framework_type);
      if (params?.search) {
        const q = params.search.toLowerCase();
        list = list.filter((t) => t.name.toLowerCase().includes(q) || t.template_text.toLowerCase().includes(q));
      }
      return demoDelay({ templates: list });
    }
    const query = new URLSearchParams();
    if (params?.side) query.append('side', params.side);
    if (params?.practice_area) query.append('practice_area', params.practice_area);
    if (params?.framework_type) query.append('framework_type', params.framework_type);
    if (params?.search) query.append('search', params.search);
    if (params?.include_builtin) query.append('include_builtin', 'true');
    const res = await fetchWithAuth(`/templates?${query.toString()}`);
    return res.json();
  },

  getById: async (id: string): Promise<{ template: ArgumentTemplate }> => {
    if (isDemoMode()) {
      const template = getDemoCreatedTemplates().find((t) => t.id === id)
        || demo.demoArgumentTemplates.find((t) => t.id === id)
        || demo.demoArgumentTemplates[0];
      return demoDelay({ template });
    }
    const res = await fetchWithAuth(`/templates/${id}`);
    return res.json();
  },

  create: async (data: Omit<ArgumentTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<{ template: ArgumentTemplate }> => {
    if (isDemoMode()) {
      const template = { ...data, id: `tmpl-${Date.now()}`, created_at: new Date().toISOString() } as ArgumentTemplate;
      const created = getDemoCreatedTemplates();
      created.unshift(template);
      saveDemoCreatedTemplates(created);
      return demoDelay({ template });
    }
    const res = await fetchWithAuth('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  update: async (id: string, data: Partial<ArgumentTemplate>): Promise<{ template: ArgumentTemplate }> => {
    if (isDemoMode()) {
      const created = getDemoCreatedTemplates();
      const idx = created.findIndex((t) => t.id === id);
      if (idx === -1) {
        const base = demo.demoArgumentTemplates.find((t) => t.id === id) || demo.demoArgumentTemplates[0];
        const template = { ...base, ...data, id } as ArgumentTemplate;
        created.unshift(template);
        saveDemoCreatedTemplates(created);
        return demoDelay({ template });
      }
      const template = { ...created[idx], ...data, id } as ArgumentTemplate;
      created[idx] = template;
      saveDemoCreatedTemplates(created);
      return demoDelay({ template });
    }
    const res = await fetchWithAuth(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) {
      const created = getDemoCreatedTemplates();
      saveDemoCreatedTemplates(created.filter((t) => t.id !== id));
      return demoDelay({ success: true });
    }
    const res = await fetchWithAuth(`/templates/${id}`, { method: 'DELETE' });
    return res.json();
  },
};

// ==================== SIMULATIONS API ====================

// In demo/preview mode we persist user-created sessions in localStorage so they
// show up across pages (case detail, run simulation, history) during a session.
const DEMO_SESSIONS_KEY = 'trialsim_demo_sessions';

function getDemoSessions(): SimulationSession[] {
  try {
    const raw = localStorage.getItem(DEMO_SESSIONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveDemoSession(session: SimulationSession) {
  try {
    const sessions = getDemoSessions();
    sessions.unshift(session);
    localStorage.setItem(DEMO_SESSIONS_KEY, JSON.stringify(sessions));
  } catch {
    // ignore
  }
}

function saveDemoSessions(sessions: SimulationSession[]) {
  try {
    localStorage.setItem(DEMO_SESSIONS_KEY, JSON.stringify(sessions));
  } catch {
    // ignore
  }
}

function enrichVerdict(v: VerdictOutput): VerdictOutput {
  return {
    ...v,
    verdict: v.verdict_distribution,
    overall_resonance: v.resonance_index,
    sentiment: v.resonance_index >= 75 ? 'favorable' : v.resonance_index >= 50 ? 'cautiously favorable' : 'unfavorable',
    panelist_reactions: v.panelist_reactions || demo.demoPersonas.slice(0, 6).map((p, i) => {
      const score = Math.max(10, Math.min(95, v.resonance_index + (i % 3 === 0 ? 12 : i % 3 === 1 ? -8 : 3)));
      const vote = score >= 60 ? 'for' : score >= 35 ? 'undecided' : 'against';
      return {
        id: p.id,
        name: p.name,
        archetype: p.archetype_tag,
        vote,
        resonance: score,
        comment: `${p.name} found the argument ${vote === 'for' ? 'persuasive' : vote === 'against' ? 'unconvincing' : 'partially persuasive'} based on their ${p.archetype_tag.toLowerCase()} perspective.`,
        reaction_type: vote === 'for' ? 'accept' : vote === 'against' ? 'reject' : 'doubt',
        sentiment_score: (score - 50) / 50,
      };
    }),
    key_themes: v.key_themes || ['Evidence quality', 'Credibility', 'Risk assessment'],
    objections: v.objections || v.objection_log.map((o) => o.concern),
    recommendations: v.recommendations || ['Strengthen with specific evidence.', 'Add expert testimony.', 'Refine narrative clarity.'],
  };
}

export const simulationsApi = {
  create: async (data: {
    case_id: string;
    pool_id?: string;
    argument_text: string;
    counter_argument_text?: string;
    counter_argument_template_id?: string;
  }): Promise<SimulationSession> => {
    if (isDemoMode()) {
      const session: SimulationSession = {
        id: `sim-${Date.now()}`,
        case_id: data.case_id,
        user_id: demo.DEMO_USER_ID,
        pool_id: null,
        argument_text: data.argument_text,
        argument_template_id: data.counter_argument_template_id || null,
        counter_argument_text: data.counter_argument_text || null,
        counter_argument_template_id: null,
        mode: 'test',
        status: 'complete',
        notes: null,
        started_at: new Date().toISOString(),
        completed_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
      };
      saveDemoSession(session);
      return demoDelay(session);
    }
    const res = await fetchWithAuth('/simulations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    const body = await res.json();
    return body.session;
  },

  getById: async (id: string): Promise<SimulationSession> => {
    if (isDemoMode()) {
      const local = getDemoSessions().find((s) => s.id === id);
      const session = local || demo.demoSimulationSessions.find((s) => s.id === id) || demo.demoSimulationSessions[0];
      return demoDelay(session);
    }
    const res = await fetchWithAuth(`/simulations/${id}`);
    const body = await res.json();
    return body.session;
  },

  getByCaseId: async (caseId: string): Promise<{ sessions: SimulationSession[] }> => {
    if (isDemoMode()) {
      const local = getDemoSessions().filter((s) => s.case_id === caseId);
      const sessions = [...local, ...demo.demoSimulationSessions.filter((s) => s.case_id === caseId)];
      return demoDelay({ sessions });
    }
    const res = await fetchWithAuth(`/simulations/case/${caseId}`);
    return res.json();
  },

  getResults: async (id: string): Promise<VerdictOutput> => {
    if (isDemoMode()) {
      const v = demo.demoVerdicts[id] || demo.demoVerdicts['sim-001'];
      return demoDelay(enrichVerdict(v));
    }
    const res = await fetchWithAuth(`/simulations/${id}/results`);
    const body = await res.json();
    return body.results;
  },

  getDebrief: async (id: string): Promise<SimulationDebrief> => {
    if (isDemoMode()) {
      return demoDelay(demo.demoDebriefs[id] || demo.demoDebriefs['sim-001']);
    }
    const res = await fetchWithAuth(`/simulations/${id}/debrief`);
    const body = await res.json();
    return body.debrief;
  },

  getPatterns: async (id: string): Promise<PatternAnalysis> => {
    if (isDemoMode()) {
      return demoDelay(demo.demoPatterns[id] || demo.demoPatterns['sim-001']);
    }
    const res = await fetchWithAuth(`/simulations/${id}/patterns`);
    const body = await res.json();
    return body.patterns;
  },

  stream: async function* (
    id: string
  ): AsyncGenerator<{ type: string; data?: unknown }, void, unknown> {
    // The backend runs the whole simulation inside GET /:id/stream and emits
    // NAMED SSE events (event: progress|complete|error), so we must use
    // addEventListener per event name — onmessage only fires for unnamed
    // events. EventSource can't send headers, hence the query-string token
    // (resolved the same way as fetchWithAuth: backend JWT, else Supabase).
    let token = sessionStorage.getItem('token');
    if (!token) {
      const { data: { session } } = await supabase.auth.getSession();
      token = session?.access_token || null;
    }

    const queue: Array<{ type: string; data?: unknown }> = [];
    let wake: (() => void) | null = null;
    let closed = false;
    const push = (evt: { type: string; data?: unknown }) => {
      queue.push(evt);
      wake?.();
    };
    const parse = (raw: unknown) => {
      try { return JSON.parse(String(raw)); } catch { return raw; }
    };

    const es = new EventSource(
      `${API_BASE_URL}/simulations/${id}/stream?token=${encodeURIComponent(token || '')}`
    );
    es.addEventListener('progress', (e: MessageEvent) => push({ type: 'progress', data: parse(e.data) }));
    es.addEventListener('complete', (e: MessageEvent) => {
      closed = true;
      es.close();
      push({ type: 'complete', data: parse(e.data) });
    });
    // Fires both for a server-sent `event: error` (has .data) and for a
    // connection failure (no .data — including the reconnect attempt after
    // the server ends the stream).
    es.addEventListener('error', (e: Event) => {
      if (closed) return;
      closed = true;
      es.close();
      const data = (e as MessageEvent).data;
      push({ type: 'error', data: data !== undefined ? parse(data) : { message: 'Simulation stream connection failed' } });
    });

    while (true) {
      while (queue.length > 0) {
        const evt = queue.shift()!;
        yield evt;
        if (evt.type === 'complete' || evt.type === 'error') return;
      }
      await new Promise<void>((resolve) => { wake = resolve; });
      wake = null;
    }
  },

  exportPdf: async (id: string): Promise<Blob> => {
    if (isDemoMode()) return demoDelay(new Blob(['Demo PDF export'], { type: 'application/pdf' }));
    const res = await fetchWithAuth(`/simulations/${id}/export`);
    if (!res.ok) {
      // Without this check a 500 JSON body gets saved as an unopenable .pdf
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `PDF export failed (${res.status})`);
    }
    return res.blob();
  },

  exportWord: async (id: string): Promise<Blob> => {
    if (isDemoMode()) return demoDelay(new Blob(['Demo Word export'], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }));
    const res = await fetchWithAuth(`/simulations/${id}/export-word`);
    return res.blob();
  },

  getAll: async (): Promise<SimulationSession[]> => {
    if (isDemoMode()) return demoDelay(demo.demoSimulationSessions);
    const res = await fetchWithAuth('/simulations');
    return res.json();
  },

  // Aliases used by legacy hooks
  // /simulations/by-case/:caseId never existed — the real per-case list route
  // is /simulations/case/:caseId (see getByCaseId above). Every call here
  // 404'd, so CaseDetailPage's "Previous Simulations" list (via useSimulations)
  // silently showed nothing, even right after a simulation was saved.
  history: async (caseId: string): Promise<{ data: { sessions: SimulationSession[] } }> => {
    if (isDemoMode()) {
      return { data: { sessions: demo.demoSimulationSessions.filter((s) => s.case_id === caseId) } };
    }
    const { sessions } = await fetchWithAuth(`/simulations/case/${caseId}`).then((r) => r.json());
    return { data: { sessions } };
  },

  start: async (id: string): Promise<void> => {
    if (isDemoMode()) return demoDelay(undefined);
    await fetchWithAuth(`/simulations/${id}/start`, { method: 'POST' });
  },

  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) {
      const sessions = getDemoSessions().filter((s) => s.id !== id);
      saveDemoSessions(sessions);
      return demoDelay({ success: true });
    }
    const res = await fetchWithAuth(`/simulations/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Could not delete simulation (${res.status})`);
    }
    return res.json();
  },

  results: async (id: string): Promise<{ data: { results: VerdictOutput } }> => {
    const results = isDemoMode()
      ? enrichVerdict(demo.demoVerdicts[id] || demo.demoVerdicts['sim-001'])
      : await fetchWithAuth(`/simulations/${id}/results`).then((r) => r.json());
    return { data: { results } };
  },

  share: async (id: string): Promise<{ url: string; token?: string; expires_at?: string; require_auth?: boolean; warning?: string }> => {
    if (isDemoMode()) {
      return demoDelay({
        url: `https://demo.sentantia.app/share/${id}`,
        token: 'demo-share-token',
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        require_auth: false,
      });
    }
    const res = await fetchWithAuth(`/simulations/${id}/share`, { method: 'POST' });
    return res.json();
  },
};

// ==================== ANALYTICS ====================
export const analyticsApi = {
  getUserSummary: async () => {
    if (isDemoMode()) return demoDelay(demo.demoUserAnalytics);
    const res = await fetchWithAuth('/simulations/analytics/user');
    return res.json();
  },
  getCaseAnalytics: async (caseId: string) => {
    if (isDemoMode()) {
      const analytics = demo.demoCaseAnalytics[caseId] || {
        totalSimulations: 1,
        averageResonance: 65,
        bestResonance: 65,
        bestSessionId: null,
        worstResonance: 65,
        worstSessionId: null,
        resonanceTrend: [],
        topObjections: [],
        themeBreakdown: [],
        verdictHistory: [],
        improvement: 0,
      };
      return demoDelay(analytics);
    }
    const res = await fetchWithAuth(`/simulations/analytics/case/${caseId}`);
    return res.json();
  },
};

export function getCaseAnalytics(caseId: string) {
  // Deterministic mock analytics based on caseId hash
  const hash = caseId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const totalSimulations = (hash % 8) + 1;
  const averageResonance = 45 + (hash % 40);
  const bestResonance = Math.min(98, averageResonance + 15 + (hash % 10));
  const worstResonance = Math.max(20, averageResonance - 15 - (hash % 10));
  const sentiments = ['strongly favorable', 'favorable', 'cautiously favorable', 'mixed', 'unfavorable'];
  const sentiment = sentiments[hash % sentiments.length];
  const themePool = ['expert testimony', 'evidence-based reasoning', 'emotional appeal', 'human impact', 'legal framework', 'duty of care', 'liability analysis', 'narrative resonance'];
  const keyThemes = themePool.slice(hash % 3, (hash % 3) + 3);
  const objectionPool = ['Lack of concrete evidence', 'Argument too brief', 'No expert testimony cited', 'Causation not clearly established', 'Damages not quantified'];
  const objections = objectionPool.slice(0, (hash % 3) + 1);
  const recommendations = [
    'Strengthen with specific evidence, studies, or expert citations.',
    'Expand the argument to provide more detailed reasoning.',
    'Consider adding human-impact narrative to increase emotional resonance.',
    'Anchor the argument more firmly in legal standards and precedent.',
    'Refine the argument structure for greater clarity and impact.',
  ];
  return {
    totalSimulations,
    averageResonance,
    bestResonance,
    worstResonance,
    sentiment,
    keyThemes,
    objections,
    recommendations: recommendations[hash % recommendations.length],
  };
}

// ==================== NOTIFICATIONS API ====================
export const notificationsApi = {
  getPreferences: async (): Promise<NotificationPreferences> => {
    if (isDemoMode()) return demoDelay(demo.demoNotificationPreferences);
    const res = await fetchWithAuth('/auth/me/notifications');
    return res.json();
  },

  updatePreferences: async (prefs: Partial<NotificationPreferences>): Promise<NotificationPreferences> => {
    if (isDemoMode()) return demoDelay({ ...demo.demoNotificationPreferences, ...prefs });
    const res = await fetchWithAuth('/auth/me/notifications', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    });
    return res.json();
  },

  sendTestEmail: async (): Promise<{ success: boolean; messageId?: string; message?: string }> => {
    if (isDemoMode()) return demoDelay({ success: true, messageId: 'demo-message-id' });
    const res = await fetchWithAuth('/auth/me/notifications/test', { method: 'POST' });
    return res.json();
  },
};

// ==================== CASES API ====================
export const casesApi = {
  getAll: async () => {
    if (isDemoMode()) return demoDelay({ cases: demo.demoCases });
    const res = await fetchWithAuth('/cases');
    return res.json();
  },
  getById: async (id: string) => {
    if (isDemoMode()) {
      const c = demo.demoCases.find((c) => c.id === id) || demo.demoCases[0];
      return demoDelay({ case: c });
    }
    const res = await fetchWithAuth(`/cases/${id}`);
    return res.json();
  },
  create: async (data: Omit<Case, 'id' | 'created_at' | 'updated_at'>): Promise<{ case: Case }> => {
    if (isDemoMode()) {
      const c: Case = {
        ...data,
        id: `case-${Date.now()}`,
        sim_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Case;
      return demoDelay({ case: c });
    }
    const res = await fetchWithAuth('/cases', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.json();
  },
  update: async (id: string, data: Partial<Case>): Promise<{ case: Case }> => {
    if (isDemoMode()) {
      const c = { ...(demo.demoCases.find((c) => c.id === id) || demo.demoCases[0]), ...data, id } as Case;
      return demoDelay({ case: c });
    }
    const res = await fetchWithAuth(`/cases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return res.json();
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/cases/${id}`, { method: 'DELETE' });
    return res.json();
  },
};

function enrichPersonas(personas: PersonaProfile[]): PersonaProfile[] {
  return personas.map((p) => ({
    ...p,
    archetype: p.archetype || p.archetype_tag,
    // Fields here are frequently missing for manually-entered panelists
    // (age/education left blank, no income_level at all) — join only the
    // pieces that actually exist rather than printing "null, undefined".
    demographics: p.demographics || [p.demographic_config?.age, p.demographic_config?.education, p.demographic_config?.occupation || p.occupation]
      .filter((v) => v !== null && v !== undefined && v !== '')
      .join(' · ') || 'Not specified',
    biases: p.biases || (p.surface_traits?.biases ? p.surface_traits.biases.split(',').map((s: string) => s.trim()) : []),
    hidden_traits: p.hidden_traits || {
      latent: p.surface_traits?.worldview,
      triggers: ['Vague language', 'Unsupported claims'],
      threshold: 'Requires clear evidence.',
      contradiction: 'Claims objectivity but follows initial impressions.',
    },
  }));
}

// ==================== PERSONAS API ====================
// Demo-mode juries are client-side only, but they still need a pool id: the
// "Add jurors" / "Add alternates" controls on Create Jury are gated on
// jury.poolId being set.
const DEMO_POOL_ID = 'pool-demo-jury';

export const personasApi = {
  getPool: async (): Promise<{ personas: PersonaProfile[] }> => {
    if (isDemoMode()) return demoDelay({ personas: enrichPersonas(demo.demoPersonas) });
    const res = await fetchWithAuth('/personas/pools');
    const data = await res.json();
    return { personas: enrichPersonas((data.personas || []) as PersonaProfile[]) };
  },

  createCustom: async (params: {
    case_id: string;
    name: string;
    age?: string;
    occupation?: string;
    education?: string;
    political_view?: string;
    notes?: string;
  }): Promise<{ persona: PersonaProfile }> => {
    if (isDemoMode()) {
      const persona = {
        id: `custom-${Date.now()}`,
        case_id: params.case_id,
        pool_id: null,
        name: params.name,
        archetype: 'Custom Juror',
        archetype_tag: 'Custom Juror',
        occupation: params.occupation || 'Not specified',
        age: params.age ? parseInt(params.age, 10) : undefined,
        is_custom: true,
        demographics: `${params.age || '—'} · ${params.education || 'Not specified'} · ${params.occupation || 'Not specified'}`,
        surface_traits: { worldview: params.political_view || 'Undecided/Unknown', decision_style: 'Balanced', biases: params.notes || '' },
        hidden_traits: { latent: params.political_view || 'Undecided/Unknown', triggers: [], threshold: 'Moderate', contradiction: '' },
        conflict_flags: [],
        created_at: new Date().toISOString(),
      } as unknown as PersonaProfile;
      return demoDelay({ persona });
    }
    const res = await fetchWithAuth('/personas/custom', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    const data = await res.json();
    return { persona: enrichPersonas([data.persona])[0] as unknown as PersonaProfile };
  },

  getCustom: async (caseId: string): Promise<{ personas: PersonaProfile[] }> => {
    if (isDemoMode()) return demoDelay({ personas: [] });
    const res = await fetchWithAuth(`/personas/custom/${caseId}`);
    const data = await res.json();
    return { personas: enrichPersonas((data.personas || []) as PersonaProfile[]) };
  },

  listPools: async (): Promise<{ data: { pools: any[] } }> => {
    if (isDemoMode()) {
      return demoDelay({
        data: {
          pools: [
            {
              id: 'pool-001',
              name: 'Default Jury',
              description: 'Balanced mix of archetypes for general testing.',
              personaIds: demo.demoPersonas.slice(0, 6).map((p) => p.id),
              createdAt: '2024-06-01T10:00:00Z',
            },
          ],
        },
      });
    }
    const res = await fetchWithAuth('/personas/pools');
    return { data: await res.json() };
  },

  generate: async (params: number | { case_id?: string; demographic_config?: any; name?: string; count?: number; alternates?: number }): Promise<{ personas: PersonaProfile[] }> => {
    const count = typeof params === 'number' ? params : params.count || 6;
    const alternates = typeof params === 'number' ? 0 : params.alternates || 0;
    if (isDemoMode()) {
      // Return EXACTLY the jury that was asked for. This used to return
      // `[...demoPersonas, ...generated]`, i.e. the 12 seed jurors plus the
      // requested count, so Create Jury reported 24 for a 12-person jury, 18
      // for 6, and 20 for 8 (always +12). Jury Selection looked correct only
      // because PanelSelectionPage defensively slices the result to its target
      // size; Create Jury rendered whatever came back.
      const total = count + alternates;
      const personas = demo
        .generateMorePersonas(total)
        .map((p, i) => ({ ...p, pool_id: DEMO_POOL_ID, is_alternate: i >= count }));
      return demoDelay({ personas: enrichPersonas(personas) });
    }
    const body = typeof params === 'number' ? { count: params } : params;
    const res = await fetchWithAuth('/personas/generate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    return { personas: enrichPersonas(data.personas || []) };
  },

  generateRandom: async (count?: number): Promise<{ personas: PersonaProfile[] }> => {
    if (isDemoMode()) {
      // Exactly `count` fresh candidates, named so they don't collide with the
      // seed set (same off-by-a-whole-jury bug as generate() above).
      const personas = demo.generateMorePersonas(count || 6, demo.demoPersonas);
      return demoDelay({ personas: enrichPersonas(personas) });
    }
    const res = await fetchWithAuth('/personas/generate-random', {
      method: 'POST',
      body: JSON.stringify({ count }),
    });
    const data = await res.json();
    return { personas: enrichPersonas(data.personas || []) };
  },

  // Grow an existing jury: pool_id targets the saved pool; as_alternate adds
  // observers (max 2 per jury) instead of seated jurors.
  expand: async (params: { pool_id: string; count: number; as_alternate?: boolean }): Promise<{ personas: PersonaProfile[] }> => {
    if (isDemoMode()) {
      const personas = demo.generateMorePersonas(params.count || 1, demo.demoPersonas).map((p) => ({ ...p, is_alternate: params.as_alternate === true }));
      return demoDelay({ personas: enrichPersonas(personas) });
    }
    const res = await fetchWithAuth('/personas/expand', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    const data = await res.json();
    return { personas: enrichPersonas(data.personas || []) };
  },

  // Remove a single juror. The backend refuses (409) once the juror has
  // already reacted in a simulation, to protect that simulation's history.
  remove: async (personaId: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/personas/${personaId}`, { method: 'DELETE' });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Could not remove juror (${res.status})`);
    }
    return res.json();
  },

  // Fetch a saved jury (persona pool) with its jurors.
  getPoolDetail: async (poolId: string): Promise<{ pool: any; personas: PersonaProfile[] }> => {
    if (isDemoMode()) {
      return demoDelay({
        pool: { id: poolId, name: 'Default Jury', panel_size: 6, case_id: null },
        personas: enrichPersonas(demo.demoPersonas.slice(0, 6)),
      });
    }
    const res = await fetchWithAuth(`/personas/pools/${poolId}`);
    const data = await res.json();
    return { pool: data.pool, personas: enrichPersonas(data.personas || []) };
  },

  generateByArchetype: async (archetype: string): Promise<{ persona: PersonaProfile }> => {
    if (isDemoMode()) {
      const p = demo.demoPersonas.find((p) => p.archetype_tag.toLowerCase().includes(archetype.toLowerCase())) || demo.demoPersonas[0];
      return demoDelay({ persona: enrichPersonas([p])[0] });
    }
    const res = await fetchWithAuth('/personas/generate-archetype', {
      method: 'POST',
      body: JSON.stringify({ archetype }),
    });
    const data = await res.json();
    return { persona: enrichPersonas([data.persona])[0] };
  },
};

// ==================== DOCUMENTS API ====================
export const documentsApi = {
  // GET /api/cases/:id/documents was never implemented on the backend (only
  // /:id/dates, /:id/deadlines, /:id/analyze, /:id/evidence-analysis exist
  // under /api/cases) — every call 404'd, and callers that swallow the error
  // (EvidenceAnalyzerPage, SimulationRunPage) silently rendered an empty
  // list. A document upload itself persists fine (POST /documents/:caseId/
  // upload really inserts into case_documents); only this refetch was wired
  // to a route that doesn't exist, so newly uploaded evidence "vanished" on
  // the very next load. The real list endpoint is GET /documents?case_id=.
  getByCaseId: async (caseId: string): Promise<{ documents: CaseDocument[] }> => {
    if (isDemoMode()) {
      const docs = demo.demoDocuments.filter((d) => d.case_id === caseId) as CaseDocument[];
      return demoDelay({ documents: docs });
    }
    const res = await fetchWithAuth(`/documents?case_id=${encodeURIComponent(caseId)}`);
    return res.json();
  },

  create: async (data: Omit<CaseDocument, 'id' | 'created_at'>): Promise<{ document: CaseDocument }> => {
    if (isDemoMode()) {
      const doc = { ...data, id: `doc-${Date.now()}`, created_at: new Date().toISOString() } as CaseDocument;
      return demoDelay({ document: doc });
    }
    const res = await fetchWithAuth('/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return res.json();
  },

  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/documents/${id}`, { method: 'DELETE' });
    return res.json();
  },

  upload: async (caseId: string, file: File): Promise<{ document: CaseDocument }> => {
    if (isDemoMode()) {
      const doc = {
        id: `doc-${Date.now()}`,
        case_id: caseId,
        user_id: demo.DEMO_USER_ID,
        doc_category: 'evidence',
        name: file.name,
        doc_type: file.name.slice(file.name.lastIndexOf('.')).toLowerCase(),
        size_bytes: file.size,
        description: 'Demo upload',
        uploaded_at: new Date().toISOString(),
        added_by: demo.DEMO_USER_ID,
      } as CaseDocument;
      return demoDelay({ document: doc });
    }
    const form = new FormData();
    form.append('file', file);
    const res = await fetchWithAuth(`/documents/${caseId}/upload`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Upload failed (${res.status})`);
    }
    return res.json();
  },

  downloadUrl: (id: string): string => `/documents/${id}/download`,

  download: async (id: string, name: string): Promise<void> => {
    const res = await fetchWithAuth(`/documents/${id}/download`);
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      throw new Error(body?.error || `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    window.URL.revokeObjectURL(url);
  },
};

// ==================== CONTRADICTIONS API ====================
// Cross-document contradiction detection (Workstream B) — see
// backend/src/routes/contradictions.js. Like deadlinesApi, these check
// res.ok and throw a readable error instead of handing callers an error
// body shaped like data.
async function contradictionJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${fallbackMessage} (${res.status})`);
  }
  return res.json();
}

function demoContradictionReport(caseId: string): { report: ContradictionReport; contradictions: ContradictionPair[] } {
  const docs = demo.demoDocuments.filter((d) => d.case_id === caseId);
  const report: ContradictionReport = {
    id: `demo-report-${caseId}`,
    case_id: caseId,
    generated_by: demo.DEMO_USER_ID,
    document_count: docs.length,
    contradiction_count: docs.length >= 2 ? 1 : 0,
    is_mock: true,
    model: 'mock',
    summary:
      docs.length >= 2
        ? `Demo preview comparing ${docs.length} documents on this case.`
        : 'Not enough documents with extracted text to compare.',
    created_at: new Date().toISOString(),
  };
  const contradictions: ContradictionPair[] =
    docs.length >= 2
      ? [
          {
            id: `demo-pair-${caseId}`,
            report_id: report.id,
            case_id: caseId,
            document_a_id: docs[0].id,
            document_a_name: docs[0].name,
            location_a: 'L1',
            statement_a: 'States the relevant event occurred at approximately 3:00 PM.',
            document_b_id: docs[1].id,
            document_b_name: docs[1].name,
            location_b: 'L1',
            statement_b: 'States the same event occurred at approximately 6:30 PM.',
            category: 'timeline',
            severity: 'high',
            relevance_score: 82,
            relevance_explanation: 'Illustrative demo contradiction — connect a case with real uploaded documents to run a live comparison.',
            created_at: new Date().toISOString(),
          },
        ]
      : [];
  return { report, contradictions };
}

export const contradictionsApi = {
  getLatest: async (caseId: string): Promise<{ report: ContradictionReport; contradictions: ContradictionPair[] }> => {
    if (isDemoMode()) return demoDelay(demoContradictionReport(caseId));
    const res = await fetchWithAuth(`/contradictions/${caseId}`);
    return contradictionJson(res, 'Failed to load contradiction report');
  },
  generate: async (caseId: string): Promise<{ report: ContradictionReport; contradictions: ContradictionPair[] }> => {
    if (isDemoMode()) return demoDelay(demoContradictionReport(caseId));
    const res = await fetchWithAuth(`/contradictions/${caseId}/generate`, { method: 'POST' });
    return contradictionJson(res, 'Failed to generate contradiction report');
  },
};

// ==================== WITNESSES API ====================
export const witnessesApi = {
  // No witnesses table or routes existed on the backend at all until now
  // (see migration 062_witnesses.sql) — every call here 404'd, so nothing
  // added through Witness Prep was ever actually saved. The real list route
  // is GET /witnesses?case_id=, not the /cases/:id/witnesses this used to
  // call (same class of bug as documentsApi.getByCaseId).
  getByCaseId: async (caseId: string): Promise<{ witnesses: Witness[] }> => {
    if (isDemoMode()) return demoDelay({ witnesses: demo.demoWitnesses.filter((w) => w.case_id === caseId) });
    const res = await fetchWithAuth(`/witnesses?case_id=${encodeURIComponent(caseId)}`);
    return res.json();
  },
  create: async (data: Omit<Witness, 'id' | 'created_at'>): Promise<{ witness: Witness }> => {
    if (isDemoMode()) {
      const witness = { ...data, id: `wit-${Date.now()}`, created_at: new Date().toISOString() } as Witness;
      return demoDelay({ witness });
    }
    const res = await fetchWithAuth('/witnesses', { method: 'POST', body: JSON.stringify(data) });
    return res.json();
  },
  update: async (id: string, data: Partial<Witness>): Promise<{ witness: Witness }> => {
    if (isDemoMode()) {
      const witness = { ...(demo.demoWitnesses.find((w) => w.id === id) || demo.demoWitnesses[0]), ...data, id } as Witness;
      return demoDelay({ witness });
    }
    const res = await fetchWithAuth(`/witnesses/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return res.json();
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/witnesses/${id}`, { method: 'DELETE' });
    return res.json();
  },
};

// ==================== COLLABORATION API ====================
// Comments (threaded, WebSocket-broadcast), invites, and activity log. See
// backend/src/routes/collaboration.js, mounted at /api/collaboration.
function demoCollabActivity(caseId: string): CaseActivity[] {
  const now = Date.now();
  return [
    { id: `act-${caseId}-1`, case_id: caseId, user_id: demo.DEMO_USER_ID, user_name: 'You', activity_type: 'ran simulation', activity_data: null, created_at: new Date(now - 3600_000).toISOString(), relative_time: '1h ago' },
    { id: `act-${caseId}-2`, case_id: caseId, user_id: demo.DEMO_USER_ID, user_name: 'You', activity_type: 'uploaded evidence', activity_data: null, created_at: new Date(now - 86400_000).toISOString(), relative_time: '1d ago' },
  ];
}

export const collaborationApi = {
  getComments: async (caseId: string): Promise<{ comments: CaseComment[] }> => {
    if (isDemoMode()) return demoDelay({ comments: [] });
    const res = await fetchWithAuth(`/collaboration/cases/${caseId}/comments`);
    return res.json();
  },
  addComment: async (caseId: string, content: string, parentId?: string): Promise<{ comment: CaseComment }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      return demoDelay({ comment: { id: `comment-${Date.now()}`, case_id: caseId, user_id: demo.DEMO_USER_ID, user_name: 'You', user_initials: 'Y', content, parent_id: parentId, replies: [], created_at: now, updated_at: now } });
    }
    const res = await fetchWithAuth(`/collaboration/cases/${caseId}/comments`, { method: 'POST', body: JSON.stringify({ content, parent_id: parentId }) });
    return res.json();
  },
  updateComment: async (id: string, content: string): Promise<{ comment: CaseComment }> => {
    const res = await fetchWithAuth(`/collaboration/comments/${id}`, { method: 'PUT', body: JSON.stringify({ content }) });
    return res.json();
  },
  deleteComment: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/collaboration/comments/${id}`, { method: 'DELETE' });
    return res.json();
  },
  getActivity: async (caseId: string): Promise<{ activities: CaseActivity[] }> => {
    if (isDemoMode()) return demoDelay({ activities: demoCollabActivity(caseId) });
    const res = await fetchWithAuth(`/collaboration/cases/${caseId}/activity`);
    return res.json();
  },
  getInvites: async (caseId: string): Promise<{ invites: CaseShare[] }> => {
    if (isDemoMode()) return demoDelay({ invites: [] });
    const res = await fetchWithAuth(`/collaboration/cases/${caseId}/invites`);
    return res.json();
  },
  // Real role vocabulary (backend/src/routes/collaboration.js validates
  // against exactly these three, defaulting to 'Attorney' otherwise) - not
  // the generic view/edit/admin the UI used before nothing here was wired
  // to a real backend.
  invite: async (caseId: string, email: string, role: 'Attorney' | 'Collaborator' | 'Viewer'): Promise<{ invite: CaseShare }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      return demoDelay({ invite: { id: `invite-${Date.now()}`, case_id: caseId, invited_by: demo.DEMO_USER_ID, invitee_email: email, role, status: 'pending', token: `token-${Date.now()}`, created_at: now, expires_at: new Date(Date.now() + 86400_000 * 30).toISOString() } });
    }
    const res = await fetchWithAuth(`/collaboration/cases/${caseId}/invite`, { method: 'POST', body: JSON.stringify({ email, role }) });
    return res.json();
  },
};

// ==================== DEADLINES API ====================
// These endpoints silently 404'd for a long time (no backend routes existed),
// so unlike most of this file they check res.ok and throw a readable error
// instead of handing callers an error body shaped like data.
async function deadlineJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${fallbackMessage} (${res.status})`);
  }
  return res.json();
}

export const deadlinesApi = {
  getAll: async (): Promise<{ deadlines: CaseDeadline[] }> => {
    if (isDemoMode()) return demoDelay({ deadlines: demo.demoDeadlines });
    const res = await fetchWithAuth('/deadlines');
    return deadlineJson(res, 'Failed to load deadlines');
  },
  getByCaseId: async (caseId: string): Promise<{ deadlines: CaseDeadline[] }> => {
    if (isDemoMode()) return demoDelay({ deadlines: demo.demoDeadlines.filter((d) => d.case_id === caseId) });
    const res = await fetchWithAuth(`/cases/${caseId}/deadlines`);
    return deadlineJson(res, 'Failed to load deadlines');
  },
  create: async (data: Omit<CaseDeadline, 'id' | 'created_at'>): Promise<{ deadline: CaseDeadline }> => {
    if (isDemoMode()) {
      const deadline = { ...data, id: `dl-${Date.now()}`, created_at: new Date().toISOString() } as CaseDeadline;
      return demoDelay({ deadline });
    }
    const res = await fetchWithAuth('/deadlines', { method: 'POST', body: JSON.stringify(data) });
    return deadlineJson(res, 'Failed to create deadline');
  },
  update: async (id: string, data: Partial<CaseDeadline>): Promise<{ deadline: CaseDeadline }> => {
    if (isDemoMode()) {
      const deadline = { ...(demo.demoDeadlines.find((d) => d.id === id) || demo.demoDeadlines[0]), ...data, id } as CaseDeadline;
      return demoDelay({ deadline });
    }
    const res = await fetchWithAuth(`/deadlines/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return deadlineJson(res, 'Failed to update deadline');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/deadlines/${id}`, { method: 'DELETE' });
    return deadlineJson(res, 'Failed to delete deadline');
  },
};

// ==================== BILLING API ====================
// Stripe Checkout (new subscriptions) + the hosted Stripe Customer Portal
// (plan changes/payment method/cancel) + the current-plan/seat-usage status
// used for the free-trial results gate and seat-cap prompts. See
// backend/src/routes/billing.js. Every write call can 501 with
// { configured: false } when Stripe env vars aren't set yet (true in every
// environment right now) — callers should show that state, not treat it as
// a crash.
export interface BillingErrorBody {
  error: string;
  configured?: boolean;
  upgradeRequired?: boolean;
}

async function billingJson<T>(res: Response, fallbackError: string): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error((body as BillingErrorBody)?.error || fallbackError) as Error & BillingErrorBody;
    err.configured = (body as BillingErrorBody)?.configured;
    err.upgradeRequired = (body as BillingErrorBody)?.upgradeRequired;
    throw err;
  }
  return body as T;
}

export const billingApi = {
  getStatus: async (): Promise<BillingStatus> => {
    if (isDemoMode()) return demoDelay(demo.demoBillingStatus);
    const res = await fetchWithAuth('/billing/status');
    return billingJson<BillingStatus>(res, 'Failed to load billing status');
  },
  createCheckoutSession: async (tier: 'solo' | 'firm' | 'growth', interval: 'monthly' | 'annual'): Promise<{ url: string }> => {
    if (isDemoMode()) return demoDelay({ url: '' });
    const res = await fetchWithAuth('/billing/checkout-session', {
      method: 'POST',
      body: JSON.stringify({ tier, interval }),
    });
    return billingJson(res, 'Failed to start checkout');
  },
  createPortalSession: async (): Promise<{ url: string }> => {
    if (isDemoMode()) return demoDelay({ url: '' });
    const res = await fetchWithAuth('/billing/portal-session', { method: 'POST' });
    return billingJson(res, 'Failed to open billing portal');
  },
};

// ==================== FIRM API ====================
// Self-serve firm member management (backend/src/routes/firm.js). Assignable
// roles here are only 'Attorney' | 'Company Admin' — never 'Super Admin'.
export interface FirmMemberRecord {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  last_login_at: string | null;
  created_at: string;
}

// 'Assistant' is the free, non-seat role: can upload documents and view
// simulations, but cannot run them (enforced server-side on both backends).
export type FirmMemberRole = 'Attorney' | 'Company Admin' | 'Assistant';

async function firmJson<T>(res: Response, fallbackMessage: string): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error || `${fallbackMessage} (${res.status})`);
  }
  return res.json();
}

export interface FirmBranding {
  id: string;
  name: string;
  slug: string;
  primary_color: string;
}

export const firmApi = {
  get: async (): Promise<{ firm: FirmBranding }> => {
    if (isDemoMode()) {
      const { id, name, slug, primary_color } = demo.demoFirm;
      return demoDelay({ firm: { id, name, slug, primary_color } });
    }
    const res = await fetchWithAuth('/firm');
    return firmJson(res, 'Failed to load firm');
  },
  update: async (input: { name: string; slug: string; primary_color: string }): Promise<{ firm: FirmBranding }> => {
    if (isDemoMode()) {
      return demoDelay({ firm: { id: demo.demoFirm.id, ...input } });
    }
    const res = await fetchWithAuth('/firm', {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    return firmJson(res, 'Failed to update firm');
  },
  getSummary: async (): Promise<{ case_count: number; member_count: number }> => {
    const res = await fetchWithAuth('/firm/summary');
    return firmJson(res, 'Failed to load firm summary');
  },
  getMembers: async (): Promise<{ members: FirmMemberRecord[] }> => {
    const res = await fetchWithAuth('/firm/members');
    return firmJson(res, 'Failed to load firm members');
  },
  addMember: async (input: {
    email: string;
    first_name: string;
    last_name: string;
    role: FirmMemberRole;
  }): Promise<{ member: FirmMemberRecord; temporaryPassword: string }> => {
    const res = await fetchWithAuth('/firm/members', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return firmJson(res, 'Failed to add member');
  },
  updateMemberRole: async (id: string, role: FirmMemberRole): Promise<{ member: FirmMemberRecord }> => {
    const res = await fetchWithAuth(`/firm/members/${id}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
    return firmJson(res, 'Failed to update member role');
  },
  removeMember: async (id: string): Promise<{ success: boolean }> => {
    const res = await fetchWithAuth(`/firm/members/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to remove member');
  },
};

// ==================== ARGUMENT VERSIONS API ====================
export const argumentVersionsApi = {
  getByCaseId: async (caseId: string): Promise<{ versions: ArgumentVersion[] }> => {
    if (isDemoMode()) {
      const repoVersions = demo.demoArgumentRepositories
        .filter((a) => a.case_id === caseId)
        .map((a) => ({
          id: a.id,
          case_id: a.case_id || caseId,
          user_id: a.user_id,
          name: a.name,
          argument_text: a.argument_text,
          framework_type: a.framework_type,
          side: a.side,
          resonance_index: a.avg_resonance,
          created_at: a.created_at,
          updated_at: a.updated_at,
        })) as ArgumentVersion[];
      // If a case has fewer than 2 saved versions, derive versions from its demo
      // simulation sessions so the Compare page always has something to compare.
      if (repoVersions.length < 2) {
        const normalizeText = (t: string) => t.trim().replace(/\s+/g, ' ');
        const repoTexts = new Set(repoVersions.map((v) => normalizeText(v.argument_text)));
        const simVersions = demo.demoSimulationSessions
          .filter((s) => s.case_id === caseId)
          .filter((s) => !repoTexts.has(normalizeText(s.argument_text || '')))
          .map((s, idx) => {
            const h = s.id.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
            return {
              id: `sim-ver-${s.id}`,
              case_id: caseId,
              user_id: s.user_id,
              name: `Simulation ${idx + 1} - ${new Date(s.created_at).toLocaleDateString()}`,
              argument_text: s.argument_text || 'No argument text',
              framework_type: 'opening' as const,
              side: 'neutral' as const,
              resonance_index: 45 + (h % 45),
              created_at: s.created_at,
              updated_at: s.created_at,
            };
          });
        const merged = [...repoVersions, ...simVersions];
        return demoDelay({ versions: merged });
      }
      return demoDelay({ versions: repoVersions });
    }
    // There is no /cases/:id/argument-versions endpoint on the backend — it
    // never existed, so this call 404'd for every real user and both
    // ArgumentVersionsPage and ComparePage silently showed "no versions"
    // regardless of a case's actual history. Build the same view demo mode
    // already does: named saves from the real argument_repository table,
    // topped up with simulation-run arguments when there aren't at least 2.
    const [repoRes, simRes] = await Promise.all([
      fetchWithAuth(`/arguments?case_id=${caseId}`).then((r) => r.json()),
      fetchWithAuth(`/simulations/case/${caseId}`).then((r) => r.json()),
    ]);
    const repoVersions: ArgumentVersion[] = ((repoRes.arguments || []) as any[]).map((a) => ({
      id: a.id,
      case_id: a.case_id || caseId,
      user_id: a.user_id,
      name: a.name,
      argument_text: a.argument_text,
      framework_type: a.framework_type,
      side: a.side,
      resonance_index: a.avg_resonance,
      created_at: a.created_at,
      updated_at: a.updated_at,
    }));
    if (repoVersions.length < 2) {
      const normalizeText = (t: string) => t.trim().replace(/\s+/g, ' ');
      const repoTexts = new Set(repoVersions.map((v) => normalizeText(v.argument_text)));
      const simVersions: ArgumentVersion[] = ((simRes.sessions || []) as any[])
        .filter((s) => s.argument_text)
        .filter((s) => !repoTexts.has(normalizeText(s.argument_text)))
        .map((s, idx) => ({
          id: `sim-ver-${s.id}`,
          case_id: caseId,
          user_id: s.user_id,
          name: `Simulation ${idx + 1} - ${new Date(s.created_at).toLocaleDateString()}`,
          argument_text: s.argument_text,
          framework_type: 'opening' as const,
          side: 'neutral' as const,
          resonance_index: s.resonance_index ?? undefined,
          created_at: s.created_at,
          updated_at: s.created_at,
        }));
      return { versions: [...repoVersions, ...simVersions] };
    }
    return { versions: repoVersions };
  },
};

// ==================== FEEDBACK API ====================
export const feedbackApi = {
  // Was /feedback/accuracy — the real route is /feedback/stats/accuracy
  // (backend/src/routes/feedback.js); every call 404'd.
  accuracyStats: async (): Promise<{ data: AccuracyStats }> => {
    if (isDemoMode()) return demoDelay({ data: demo.demoAccuracyStats });
    const res = await fetchWithAuth('/feedback/stats/accuracy');
    return res.json();
  },
  getByCaseId: async (caseId: string): Promise<{ feedback: PostTrialFeedback[] }> => {
    if (isDemoMode()) return demoDelay({ feedback: demo.demoPostTrialFeedbacks.filter((f) => f.case_id === caseId) });
    const res = await fetchWithAuth(`/cases/${caseId}/feedback`);
    return res.json();
  },
  // Was POST /feedback with case_id in the body — the only create route is
  // POST /cases/:id/feedback (case id in the URL); every call 404'd.
  create: async (data: Omit<PostTrialFeedback, 'id' | 'created_at'>): Promise<{ feedback: PostTrialFeedback }> => {
    if (isDemoMode()) {
      const feedback = { ...data, id: `ptf-${Date.now()}`, created_at: new Date().toISOString() } as PostTrialFeedback;
      return demoDelay({ feedback });
    }
    const res = await fetchWithAuth(`/cases/${data.case_id}/feedback`, { method: 'POST', body: JSON.stringify(data) });
    return res.json();
  },
};

// ==================== PANELISTS API ====================
export const panelistsApi = {
  bestBackgrounds: async (params: { caseType: string; side: string; desiredOutcome: string }): Promise<{ data: BestBackgroundResult }> => {
    if (isDemoMode()) return demoDelay({ data: demo.demoBestBackgrounds });
    const res = await fetchWithAuth('/panelists/best-backgrounds', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return res.json();
  },
  // The real route (backend/src/routes/panelists.js POST /generate) 400s
  // without case_id in the body — BestBackgroundsFinder had a caseId prop
  // available but never included it, so every real call failed and was
  // swallowed by the surrounding try/catch.
  generate: async (params: { case_id: string; desiredOutcome: string; archetypes?: string[]; caseContext?: string }): Promise<{ data: PersonaProfile }> => {
    if (isDemoMode()) {
      const archetype = params.archetypes?.[0] || 'Skeptical Analyst';
      const p = demo.demoPersonas.find((p) => p.archetype_tag.toLowerCase().includes(archetype.toLowerCase())) || demo.demoPersonas[0];
      return demoDelay({ data: enrichPersonas([p])[0] });
    }
    const res = await fetchWithAuth('/panelists/generate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return res.json();
  },
};

// ==================== EVIDENCE API ====================
export const evidenceApi = {
  // Was POST /evidence/:documentId/analyze — evidence.js only has /compare;
  // the real analyze route is POST /documents/:id/analyze, which also
  // doesn't wrap its response in a `data` envelope. Every real call 404'd.
  analyze: async (documentId: string): Promise<{ analysis: EvidenceAnalysis; document: { id: string; name: string } }> => {
    if (isDemoMode()) return demoDelay({ analysis: demo.demoEvidenceAnalysis, document: { id: documentId, name: 'Document' } });
    const res = await fetchWithAuth(`/documents/${documentId}/analyze`, { method: 'POST' });
    return res.json();
  },
  // Was GET /evidence/compare?docA=&docB= — the real route is
  // POST /evidence/compare with { docIdA, docIdB } in the body (and no
  // `data` envelope in the response). Every real call 404'd/400'd.
  compare: async (
    docAId: string,
    docBId: string
  ): Promise<{ docA: any; docB: any; recommendation: string; combined_strategy: string }> => {
    if (isDemoMode()) {
      return demoDelay({
        docA: { id: docAId, name: 'Document A', persuasive_strength: 82, credibility_score: 76 },
        docB: { id: docBId, name: 'Document B', persuasive_strength: 64, credibility_score: 81 },
        recommendation: 'Document A is stronger for opening; Document B supports credibility on cross.',
        combined_strategy: 'Use Document A to establish the breach, then Document B to corroborate authenticity.',
      });
    }
    const res = await fetchWithAuth('/evidence/compare', {
      method: 'POST',
      body: JSON.stringify({ docIdA: docAId, docIdB: docBId }),
    });
    return res.json();
  },
  // Case-level evidentiary gap analysis. Real mode runs the Convex action
  // directly (EvidenceAnalyzerPage calls api.documentsActions.analyzeEvidenceGaps),
  // so these are demo-mode-only fallbacks. Real mode never reaches them; if it
  // did, returning null here is safer than fabricating scores (see the page's
  // NOTE block on the removed hash-of-the-id fake analysis).
  analyzeGaps: async (): Promise<{ analysis: EvidenceGapAnalysis }> => {
    return demoDelay({ analysis: demo.demoEvidenceGapAnalysis });
  },
  getGapAnalysis: async (): Promise<{ analysis: EvidenceGapAnalysis | null; analyzedAt: string | null }> => {
    return demoDelay({ analysis: demo.demoEvidenceGapAnalysis, analyzedAt: new Date().toISOString() });
  },
};

// ==================== TIMELINE API ====================
// Response shapes below match backend/src/routes/feedback.js exactly
// (res.json({ events }) / { event } / { templates }, no `data` wrapper) -
// the previous version of this client assumed a `{ data: { ... } }`
// envelope the backend never sends, so every real (non-demo) call here
// silently returned undefined at the call site.
export const timelineApi = {
  list: async (caseId: string): Promise<{ events: TrialTimelineEvent[] }> => {
    if (isDemoMode()) return demoDelay({ events: demo.demoTimelineEvents.filter((e) => e.case_id === caseId) });
    const res = await fetchWithAuth(`/cases/${caseId}/timeline`);
    return res.json();
  },
  create: async (caseId: string, data: Partial<TrialTimelineEvent>): Promise<{ event: TrialTimelineEvent }> => {
    if (isDemoMode()) {
      const event = { ...data, id: `tev-${Date.now()}`, case_id: caseId, user_id: demo.DEMO_USER_ID, is_completed: false, created_at: new Date().toISOString() } as TrialTimelineEvent;
      return demoDelay({ event });
    }
    const res = await fetchWithAuth(`/cases/${caseId}/timeline`, { method: 'POST', body: JSON.stringify(data) });
    return res.json();
  },
  update: async (id: string, data: Partial<TrialTimelineEvent>): Promise<{ event: TrialTimelineEvent }> => {
    if (isDemoMode()) {
      const event = { ...(demo.demoTimelineEvents.find((e) => e.id === id) || demo.demoTimelineEvents[0]), ...data, id } as TrialTimelineEvent;
      return demoDelay({ event });
    }
    const res = await fetchWithAuth(`/timeline/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return res.json();
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/timeline/${id}`, { method: 'DELETE' });
    return res.json();
  },
  complete: async (id: string): Promise<{ event: TrialTimelineEvent }> => {
    if (isDemoMode()) {
      const event = { ...(demo.demoTimelineEvents.find((e) => e.id === id) || demo.demoTimelineEvents[0]), is_completed: true, id } as TrialTimelineEvent;
      return demoDelay({ event });
    }
    const res = await fetchWithAuth(`/timeline/${id}/complete`, { method: 'POST' });
    return res.json();
  },
  checklistTemplates: async (): Promise<{ templates: ChecklistTemplate[] }> => {
    if (isDemoMode()) return demoDelay({ templates: demo.demoChecklistTemplates });
    const res = await fetchWithAuth('/checklist-templates');
    return res.json();
  },
  applyChecklist: async (caseId: string, templateId: string): Promise<{ events: TrialTimelineEvent[] }> => {
    if (isDemoMode()) {
      const template = demo.demoChecklistTemplates.find((t) => t.id === templateId) || demo.demoChecklistTemplates[0];
      const events = (template.items as Array<{ title: string; description?: string; priority?: string; event_type?: string }>).map((item, i) => ({
        id: `tev-${Date.now()}-${i}`,
        case_id: caseId,
        user_id: demo.DEMO_USER_ID,
        event_type: item.event_type || 'task',
        title: item.title,
        description: item.description || '',
        is_completed: false,
        priority: item.priority || 'medium',
        created_at: new Date().toISOString(),
      })) as TrialTimelineEvent[];
      return demoDelay({ events });
    }
    // Real path: backend/src/routes/feedback.js mounts this at
    // POST /cases/:id/checklist/apply, body key `templateId` (camelCase) -
    // not `/apply-checklist` / `template_id`, which the old client sent.
    const res = await fetchWithAuth(`/cases/${caseId}/checklist/apply`, {
      method: 'POST',
      body: JSON.stringify({ templateId }),
    });
    return res.json();
  },
};

export interface ArgumentWeaknessCategoryScore {
  category: 'Logic' | 'Evidence' | 'Emotion' | 'Credibility' | 'Jury';
  score: number;
  questions: string[];
}

export interface ArgumentWeaknessItem {
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  category: 'Logic' | 'Evidence' | 'Emotion' | 'Credibility' | 'Jury';
  description: string;
  fixSuggestion: string;
}

export interface ArgumentWeaknessAnalysis {
  overallRiskScore: number;
  riskLevel: 'Critical' | 'High' | 'Medium' | 'Low';
  categoryScores: ArgumentWeaknessCategoryScore[];
  weaknesses: ArgumentWeaknessItem[];
  strengths: { description: string }[];
  fixSummary: string;
}

// ==================== AI CONTENT GENERATION API ====================
// Real Claude-backed generation for trial-prep pages that used to fabricate
// their output client-side via a hash-of-caseId generator (round-2
// fake-data audit's "lower-severity, same pattern" batch). Every method
// here is stateless — nothing is persisted server-side, matching each
// page's own local-draft nature.

export interface OpeningStatementCategoryScore { category: string; score: number; feedback: string; }
export interface OpeningStatementArchetypeScore { archetype: string; score: number; reaction: 'positive' | 'negative' | 'neutral'; triggerWords: string[]; }
export interface OpeningStatementAnalysis {
  structureScore: number;
  categoryScores: OpeningStatementCategoryScore[];
  missingElements: string[];
  archetypeScores: OpeningStatementArchetypeScore[];
  optimizedText: string;
}

export interface CrossExamQuestionResult { question: string; strategy: string; difficulty: string; expectedAnswer: string; followUps: string[]; }
export interface CrossExamResult {
  questions: CrossExamQuestionResult[];
  strategyPanel: { vulnerabilities: string[]; keyThemes: string[]; documentsReady: string[]; concede: string[]; fight: string[] };
}

export interface ClosingArgumentResult {
  closingText: string;
  strengthScore: { overall: number; evidenceCoverage: number; themeConsistency: number; credibilityLeverage: number };
  themeFeedback: { score: number; assessment: string; suggestion: string };
}

export interface CaseStrategyResult {
  strengths: string[]; weaknesses: string[]; opportunities: string[]; threats: string[];
  legalTheory: string; recommendations: string[];
  readinessScore: number; settlementProbability: number; trialWinProbability: number;
  keyFacts: string[];
}

export interface MediationPrepResult {
  ourPosition: string; theirPosition: string;
  commonGround: string[]; dealBreakers: string[];
  negotiationStrategy: string; openingStatementDraft: string;
}

export interface DepositionOutlineQuestion { question: string; expectedAnswer: string; priority: string; }
export interface DepositionOutlineSection { title: string; questions: DepositionOutlineQuestion[]; }
export interface DepositionOutlineResult { sections: DepositionOutlineSection[]; }

export interface PanelQuestionResult { question: string; category: string; probability: string; archetype: string; suggestedAnswer: string; evidenceFlag: string; }
export interface PanelQuestionsResult { questions: PanelQuestionResult[]; }

export interface PresentationSpecificPhrase { phrase: string; effect: 'positive' | 'negative' | 'neutral'; explanation: string; }
export interface PresentationRevisionSuggestion { dimension: 'Clarity' | 'Emotional Appeal' | 'Logical Structure' | 'Evidence Support' | 'Persuasiveness'; suggestion: string; priority: 'high' | 'medium' | 'low'; }
export interface PresentationAnalysisResult {
  summary: string;
  strengths: string[];
  weaknesses: string[];
  specificPhrases: PresentationSpecificPhrase[];
  revisionSuggestions: PresentationRevisionSuggestion[];
  jurorInsights: string[];
}

export const aiContentApi = {
  analyzeOpeningStatement: async (data: { statement_text: string; case_id?: string }): Promise<{ analysis: OpeningStatementAnalysis }> => {
    if (isDemoMode()) {
      return demoDelay({
        analysis: {
          structureScore: 50,
          categoryScores: ['Hook', 'Theme Statement', 'Roadmap', 'Credibility Establishment', 'Emotional Connection'].map((category) => ({ category, score: 50, feedback: 'Demo mode does not run real AI analysis.' })),
          missingElements: [],
          archetypeScores: ['Conservative', 'Empathetic', 'Analytical', 'Authoritarian', 'Skeptical'].map((archetype) => ({ archetype, score: 50, reaction: 'neutral' as const, triggerWords: [] })),
          optimizedText: data.statement_text,
        },
      });
    }
    const res = await fetchWithAuth('/ai-content/opening-statement', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to analyze opening statement');
  },
  generateCrossExam: async (data: { witness_id: string; statement_text?: string }): Promise<{ result: CrossExamResult }> => {
    if (isDemoMode()) {
      return demoDelay({
        result: {
          questions: [
            {
              question: 'Demo mode does not run real AI analysis.',
              strategy: 'Foundation',
              difficulty: 'Easy',
              expectedAnswer: 'Demo mode does not run real AI analysis.',
              followUps: [],
            },
          ],
          strategyPanel: {
            vulnerabilities: ['Demo mode does not run real AI analysis.'],
            keyThemes: ['Demo mode does not run real AI analysis.'],
            documentsReady: ['Demo mode does not run real AI analysis.'],
            concede: ['Demo mode does not run real AI analysis.'],
            fight: ['Demo mode does not run real AI analysis.'],
          },
        },
      });
    }
    const res = await fetchWithAuth('/ai-content/cross-exam', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to generate cross-examination questions');
  },
  generateClosingArgument: async (data: { case_id: string; theme_sentence?: string }): Promise<{ result: ClosingArgumentResult }> => {
    if (isDemoMode()) {
      return demoDelay({ result: { closingText: 'Demo mode does not run real AI analysis.', strengthScore: { overall: 50, evidenceCoverage: 15, themeConsistency: 10, credibilityLeverage: 10 }, themeFeedback: { score: 50, assessment: 'MODERATE', suggestion: 'Demo mode does not run real AI analysis.' } } });
    }
    const res = await fetchWithAuth('/ai-content/closing-argument', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to generate closing argument');
  },
  generateCaseStrategy: async (data: { case_id: string }): Promise<{ strategy: CaseStrategyResult }> => {
    if (isDemoMode()) {
      return demoDelay({ strategy: { strengths: [], weaknesses: [], opportunities: [], threats: [], legalTheory: 'Demo mode does not run real AI analysis.', recommendations: [], readinessScore: 50, settlementProbability: 50, trialWinProbability: 50, keyFacts: [] } });
    }
    const res = await fetchWithAuth('/ai-content/case-strategy', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to generate case strategy');
  },
  generateMediationPrep: async (data: { case_id: string; demand?: number; offer?: number; target?: number; walk_away?: number; medical_expenses?: number; lost_wages?: number }): Promise<{ result: MediationPrepResult }> => {
    if (isDemoMode()) {
      return demoDelay({ result: { ourPosition: 'Demo mode does not run real AI analysis.', theirPosition: 'Demo mode does not run real AI analysis.', commonGround: [], dealBreakers: [], negotiationStrategy: 'Demo mode does not run real AI analysis.', openingStatementDraft: 'Demo mode does not run real AI analysis.' } });
    }
    const res = await fetchWithAuth('/ai-content/mediation-prep', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to generate mediation prep content');
  },
  generateDepositionOutline: async (data: { witness_id: string }): Promise<{ outline: DepositionOutlineResult }> => {
    if (isDemoMode()) return demoDelay({ outline: { sections: [] } });
    const res = await fetchWithAuth('/ai-content/deposition-outline', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to generate deposition outline');
  },
  generatePanelQuestions: async (data: { case_id: string }): Promise<{ result: PanelQuestionsResult }> => {
    if (isDemoMode()) return demoDelay({ result: { questions: [] } });
    const res = await fetchWithAuth('/ai-content/panel-questions', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to generate panel questions');
  },
  /** Demo-only stub for Exhibit Impact. Real mode uses Convex
   *  aiContentActions.predictExhibitImpact. */
  predictExhibitImpact: async (data: {
    case_id: string;
  }): Promise<{
    exhibits: Array<{
      documentId: string;
      admissibility: number;
      relevance: number;
      emotional: number;
      memorability: number;
      rationale?: string;
      type?: string;
    }>;
    strategyNotes: string[];
  }> => {
    if (isDemoMode()) {
      const docs = demo.demoDocuments?.filter((d: { case_id: string }) => d.case_id === data.case_id) ?? [];
      return demoDelay({
        exhibits: docs.map((d: { id: string; name: string }, i: number) => ({
          documentId: d.id,
          admissibility: 70 + (i % 3) * 5,
          relevance: 65 + (i % 4) * 6,
          emotional: 55 + (i % 5) * 7,
          memorability: 60 + (i % 3) * 8,
          rationale: `Demo score for ${d.name} (no live AI).`,
        })),
        strategyNotes: [
          'Demo mode does not run real AI analysis.',
          'Lead with the highest-emotion exhibit in opening.',
        ],
      });
    }
    const res = await fetchWithAuth('/ai-content/exhibit-impact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return firmJson(res, 'Failed to predict exhibit impact');
  },

  analyzePresentation: async (data: { session_id: string }): Promise<{ analysis: PresentationAnalysisResult }> => {
    if (isDemoMode()) {
      return demoDelay({
        analysis: {
          summary: 'Demo mode does not run real AI analysis. This is a placeholder summary.',
          strengths: ['Demo placeholder strength 1', 'Demo placeholder strength 2'],
          weaknesses: ['Demo placeholder weakness 1', 'Demo placeholder weakness 2'],
          specificPhrases: [
            { phrase: 'Demo phrase', effect: 'neutral' as const, explanation: 'Demo mode does not run real AI analysis.' },
          ],
          revisionSuggestions: [
            { dimension: 'Clarity' as const, suggestion: 'Demo placeholder suggestion', priority: 'medium' as const },
          ],
          jurorInsights: ['Demo mode does not run real AI analysis.'],
        },
      });
    }
    const res = await fetchWithAuth('/ai-content/presentation-analysis', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return firmJson(res, 'Failed to analyze presentation');
  },
};

// ==================== ARGUMENT REPOSITORY API ====================
export const argumentRepositoryApi = {
  getByCaseId: async (caseId: string): Promise<{ arguments: ArgumentRepository[] }> => {
    if (isDemoMode()) return demoDelay({ arguments: demo.demoArgumentRepositories.filter((a) => a.case_id === caseId) });
    const res = await fetchWithAuth(`/arguments?case_id=${caseId}`);
    return res.json();
  },
  create: async (data: Omit<ArgumentRepository, 'id' | 'created_at' | 'updated_at'>): Promise<{ argument: ArgumentRepository }> => {
    if (isDemoMode()) {
      const arg = { ...data, id: `arg-${Date.now()}`, created_at: new Date().toISOString(), updated_at: new Date().toISOString() } as ArgumentRepository;
      return demoDelay({ argument: arg });
    }
    const res = await fetchWithAuth('/arguments', { method: 'POST', body: JSON.stringify(data) });
    return res.json();
  },
  update: async (id: string, data: Partial<ArgumentRepository>): Promise<{ argument: ArgumentRepository }> => {
    if (isDemoMode()) {
      const existing = demo.demoArgumentRepositories.find((a) => a.id === id) || demo.demoArgumentRepositories[0];
      const arg = { ...existing, ...data, id, updated_at: new Date().toISOString() } as ArgumentRepository;
      return demoDelay({ argument: arg });
    }
    const res = await fetchWithAuth(`/arguments/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return res.json();
  },
  remove: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/arguments/${id}`, { method: 'DELETE' });
    return res.json();
  },
  // Real Claude-backed analysis (backend/src/services/ai.js
  // analyzeArgumentWeaknesses) — replaced a hash-of-caseId generator that
  // fabricated risk scores and weaknesses while the page's own UI called
  // it "AI Analysis."
  analyzeWeaknesses: async (data: { argument_text: string; case_id?: string }): Promise<{ analysis: ArgumentWeaknessAnalysis }> => {
    if (isDemoMode()) {
      return demoDelay({
        analysis: {
          overallRiskScore: 50,
          riskLevel: 'Medium',
          categoryScores: [
            { category: 'Logic', score: 50, questions: ['Are there logical fallacies?'] },
            { category: 'Evidence', score: 50, questions: ['Does every claim have backing evidence?'] },
            { category: 'Emotion', score: 50, questions: ['Will it resonate with this jury?'] },
            { category: 'Credibility', score: 50, questions: ['Are citations strong?'] },
            { category: 'Jury', score: 50, questions: ['Will this land with a mixed jury pool?'] },
          ],
          weaknesses: [],
          strengths: [],
          fixSummary: 'Demo mode does not run real AI analysis.',
        },
      });
    }
    const res = await fetchWithAuth('/arguments/weakness-analysis', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to analyze argument');
  },
};

// ==================== CLIENT INTAKE API ====================
export interface ClientIntake {
  id: string;
  case_id: string | null;
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
  case_type: string;
  case_description: string;
  desired_outcome: string;
  urgency: 'Low' | 'Medium' | 'High';
  has_attorney: 'Yes' | 'No';
  agreed_to_contact: boolean;
  created_at: string;
}

// ClientIntakePage used to write a fabricated "case" to a localStorage key
// (sentantia_cases) nothing else in the app ever read — a completed intake
// never became a real case, and the intake detail vanished on refresh. This
// creates the real case via casesApi.create, then records the richer
// intake-specific fields (contact info, urgency, desired outcome) here.
export const intakesApi = {
  create: async (data: Omit<ClientIntake, 'id' | 'user_id' | 'created_at'>): Promise<{ intake: ClientIntake }> => {
    if (isDemoMode()) {
      const intake = { ...data, id: `intake-${Date.now()}`, user_id: 'demo-user', created_at: new Date().toISOString() } as ClientIntake;
      return demoDelay({ intake });
    }
    const res = await fetchWithAuth('/intakes', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to save intake');
  },
};

// ==================== CASE NOTES API ====================
export interface CaseNote {
  id: string;
  case_id: string;
  user_id: string;
  title: string;
  content: string;
  category: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

// CaseNotesPage used to seed from a hardcoded MOCK_NOTES constant into
// component state — every add/edit/delete/pin only ever touched local
// state, so notes vanished on refresh regardless of which case was open.
export const notesApi = {
  getByCaseId: async (caseId: string): Promise<{ notes: CaseNote[] }> => {
    if (isDemoMode()) return demoDelay({ notes: [] });
    const res = await fetchWithAuth(`/notes?case_id=${encodeURIComponent(caseId)}`);
    return firmJson(res, 'Failed to load notes');
  },
  create: async (data: { case_id: string; title?: string; content?: string; category?: string }): Promise<{ note: CaseNote }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const note = { id: `note-${Date.now()}`, user_id: 'demo-user', title: 'New Note', content: '', category: 'General', pinned: false, created_at: now, updated_at: now, ...data } as CaseNote;
      return demoDelay({ note });
    }
    const res = await fetchWithAuth('/notes', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to create note');
  },
  update: async (id: string, data: Partial<Pick<CaseNote, 'title' | 'content' | 'category' | 'pinned'>>): Promise<{ note: CaseNote }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      return demoDelay({ note: { id, updated_at: now, ...data } as CaseNote });
    }
    const res = await fetchWithAuth(`/notes/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update note');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/notes/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete note');
  },
};

// ==================== CASE TASKS API ====================
export interface CaseTask {
  id: string;
  case_id: string;
  user_id: string;
  title: string;
  description: string;
  category: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Not Started' | 'In Progress' | 'Completed';
  due_date: string;
  assignee_name: string | null;
  created_at: string;
  updated_at: string;
}

// TaskManagementPage used to seed from a hardcoded MOCK_TASKS constant into
// component state — every add/edit/complete/delete only ever touched local
// state, so the entire task list reset on refresh.
export const tasksApi = {
  getAll: async (): Promise<{ tasks: CaseTask[] }> => {
    if (isDemoMode()) return demoDelay({ tasks: [] });
    const res = await fetchWithAuth('/tasks');
    return firmJson(res, 'Failed to load tasks');
  },
  create: async (data: { case_id: string; title: string; description?: string; category?: string; priority?: string; status?: string; due_date: string; assignee_name?: string }): Promise<{ task: CaseTask }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const task = { id: `task-${Date.now()}`, user_id: 'demo-user', description: '', category: 'Other', priority: 'Medium', status: 'Not Started', assignee_name: null, created_at: now, updated_at: now, ...data } as CaseTask;
      return demoDelay({ task });
    }
    const res = await fetchWithAuth('/tasks', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to create task');
  },
  update: async (id: string, data: Partial<Pick<CaseTask, 'title' | 'description' | 'category' | 'priority' | 'status' | 'due_date' | 'assignee_name'>>): Promise<{ task: CaseTask }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      return demoDelay({ task: { id, updated_at: now, ...data } as CaseTask });
    }
    const res = await fetchWithAuth(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update task');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/tasks/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete task');
  },
};

// ==================== TIME TRACKING API ====================
export interface TimeEntryRecord {
  id: string;
  case_id: string;
  user_id: string;
  description: string;
  duration_minutes: number;
  date: string;
  billable: boolean;
  created_at: string;
}

// TimeTrackingPage used to seed from a hardcoded MOCK_ENTRIES constant into
// component state — Stop & Save, manual Add Entry, edit, delete, and the
// billable toggle all only ever touched local state.
export const timeApi = {
  getAll: async (): Promise<{ entries: TimeEntryRecord[] }> => {
    if (isDemoMode()) return demoDelay({ entries: [] });
    const res = await fetchWithAuth('/time');
    return firmJson(res, 'Failed to load time entries');
  },
  create: async (data: { case_id: string; description?: string; duration_minutes: number; date: string; billable?: boolean }): Promise<{ entry: TimeEntryRecord }> => {
    if (isDemoMode()) {
      const entry = { id: `time-${Date.now()}`, user_id: 'demo-user', description: '', billable: true, created_at: new Date().toISOString(), ...data } as TimeEntryRecord;
      return demoDelay({ entry });
    }
    const res = await fetchWithAuth('/time', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to create time entry');
  },
  // case_id is intentionally not editable here — the backend treats it as
  // immutable per entry, matching every other case-scoped resource
  // (witnesses, notes, tasks). Reassigning an entry's case means deleting
  // and recreating it.
  update: async (id: string, data: Partial<Pick<TimeEntryRecord, 'description' | 'duration_minutes' | 'date' | 'billable'>>): Promise<{ entry: TimeEntryRecord }> => {
    if (isDemoMode()) return demoDelay({ entry: { id, ...data } as TimeEntryRecord });
    const res = await fetchWithAuth(`/time/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update time entry');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/time/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete time entry');
  },
};

// ==================== CLIENT COMMUNICATIONS API ====================
export interface ClientCommunicationRecord {
  id: string;
  case_id: string;
  user_id: string;
  type: string;
  date: string;
  subject: string;
  summary: string;
  direction: 'outgoing' | 'incoming';
  follow_up_needed: boolean;
  follow_up_date: string | null;
  contact_name: string;
  contact_role: string;
  created_at: string;
  updated_at: string;
}

// ClientCommunicationsPage used to seed from a hardcoded MOCK_COMMUNICATIONS
// constant into component state — every log/edit/delete only ever touched
// local state.
export const communicationsApi = {
  getAll: async (): Promise<{ communications: ClientCommunicationRecord[] }> => {
    if (isDemoMode()) return demoDelay({ communications: [] });
    const res = await fetchWithAuth('/communications');
    return firmJson(res, 'Failed to load communications');
  },
  create: async (data: {
    case_id: string; type?: string; date: string; subject: string; summary?: string;
    direction?: string; follow_up_needed?: boolean; follow_up_date?: string | null;
    contact_name?: string; contact_role?: string;
  }): Promise<{ communication: ClientCommunicationRecord }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const communication = {
        id: `comm-${Date.now()}`, user_id: 'demo-user', type: 'Email', summary: '', direction: 'outgoing',
        follow_up_needed: false, follow_up_date: null, contact_name: '', contact_role: 'Client',
        created_at: now, updated_at: now, ...data,
      } as ClientCommunicationRecord;
      return demoDelay({ communication });
    }
    const res = await fetchWithAuth('/communications', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to log communication');
  },
  update: async (
    id: string,
    data: Partial<Pick<ClientCommunicationRecord, 'type' | 'date' | 'subject' | 'summary' | 'direction' | 'follow_up_needed' | 'follow_up_date' | 'contact_name' | 'contact_role'>>
  ): Promise<{ communication: ClientCommunicationRecord }> => {
    if (isDemoMode()) return demoDelay({ communication: { id, ...data } as ClientCommunicationRecord });
    const res = await fetchWithAuth(`/communications/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update communication');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/communications/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete communication');
  },
};

// ==================== APPELLATE TRACKER API ====================
export interface AppealCaseRecord {
  id: string;
  case_id: string;
  user_id: string;
  lower_court_judgment_date: string;
  appellant: string;
  appellee: string;
  court: string;
  docket_number: string;
  priority: 'High' | 'Medium' | 'Low';
  status: 'Active' | 'Pending' | 'Decided' | 'Settled';
  created_at: string;
  updated_at: string;
}

// ==================== DISCOVERY TRACKER API ====================
export interface DiscoveryItemRecord {
  id: string;
  case_id: string;
  user_id: string;
  direction: 'outgoing' | 'incoming';
  type: string;
  description: string;
  date: string;
  due_date: string;
  status: string;
  response_status: string;
  attachments: number;
  notes: string;
  created_at: string;
  updated_at: string;
}

// DiscoveryTrackerPage used to seed from a hardcoded MOCK_DISCOVERY constant
// into component state — every add/edit/delete only ever touched local
// state.
export const discoveryApi = {
  getAll: async (): Promise<{ items: DiscoveryItemRecord[] }> => {
    if (isDemoMode()) return demoDelay({ items: [] });
    const res = await fetchWithAuth('/discovery');
    return firmJson(res, 'Failed to load discovery items');
  },
  create: async (data: {
    case_id: string; direction?: string; type?: string; description: string;
    date: string; due_date: string; status?: string; response_status?: string;
    attachments?: number; notes?: string;
  }): Promise<{ item: DiscoveryItemRecord }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const item = {
        id: `disc-${Date.now()}`, user_id: 'demo-user', direction: 'outgoing', type: 'Request for Production',
        status: 'Pending', response_status: 'Not Yet', attachments: 0, notes: '',
        created_at: now, updated_at: now, ...data,
      } as DiscoveryItemRecord;
      return demoDelay({ item });
    }
    const res = await fetchWithAuth('/discovery', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to create discovery item');
  },
  update: async (
    id: string,
    data: Partial<Pick<DiscoveryItemRecord, 'type' | 'description' | 'date' | 'due_date' | 'status' | 'response_status' | 'attachments' | 'notes'>>
  ): Promise<{ item: DiscoveryItemRecord }> => {
    if (isDemoMode()) return demoDelay({ item: { id, ...data } as DiscoveryItemRecord });
    const res = await fetchWithAuth(`/discovery/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update discovery item');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/discovery/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete discovery item');
  },
};

// ==================== INVOICE GENERATOR API ====================
export interface InvoiceLineItemRecord {
  description: string;
  date: string;
  hours: number;
  rate: number;
}

export interface InvoiceRecord {
  id: string;
  case_id: string;
  user_id: string;
  invoice_number: string;
  client_name: string;
  client_address: string;
  invoice_date: string;
  due_date: string;
  hourly_rate: number;
  line_items: InvoiceLineItemRecord[];
  tax_percent: number;
  notes: string;
  status: 'Draft' | 'Sent' | 'Paid' | 'Overdue';
  total: number;
  created_at: string;
  updated_at: string;
}

// InvoiceGeneratorPage used to seed from a hardcoded createMockInvoices()
// constant into component state — "Mark as Sent" only ever appended to
// local state, nothing persisted. Total is always computed server-side.
export const invoicesApi = {
  getAll: async (): Promise<{ invoices: InvoiceRecord[] }> => {
    if (isDemoMode()) return demoDelay({ invoices: [] });
    const res = await fetchWithAuth('/invoices');
    return firmJson(res, 'Failed to load invoices');
  },
  create: async (data: {
    case_id: string; invoice_number: string; client_name?: string; client_address?: string;
    invoice_date: string; due_date: string; hourly_rate?: number; line_items?: InvoiceLineItemRecord[];
    tax_percent?: number; notes?: string; status?: string;
  }): Promise<{ invoice: InvoiceRecord }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const items = data.line_items || [];
      const subtotal = items.reduce((sum, li) => sum + li.hours * li.rate, 0);
      const total = subtotal + subtotal * ((data.tax_percent || 0) / 100);
      const invoice = {
        id: `inv-${Date.now()}`, user_id: 'demo-user', client_name: '', client_address: '',
        hourly_rate: 0, line_items: items, tax_percent: 0, notes: '', status: 'Draft',
        created_at: now, updated_at: now, ...data, total,
      } as InvoiceRecord;
      return demoDelay({ invoice });
    }
    const res = await fetchWithAuth('/invoices', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to create invoice');
  },
  update: async (
    id: string,
    data: Partial<Pick<InvoiceRecord, 'invoice_number' | 'client_name' | 'client_address' | 'invoice_date' | 'due_date' | 'hourly_rate' | 'line_items' | 'tax_percent' | 'notes' | 'status'>>
  ): Promise<{ invoice: InvoiceRecord }> => {
    if (isDemoMode()) return demoDelay({ invoice: { id, ...data } as InvoiceRecord });
    const res = await fetchWithAuth(`/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update invoice');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/invoices/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete invoice');
  },
};

// ==================== EXPERT WITNESS API ====================
export interface ExpertNotableCase {
  title: string;
  outcome: 'Win' | 'Loss' | 'Settlement';
  year: string;
}

export interface ExpertPublication {
  title: string;
  journal: string;
  year: string;
}

export interface ExpertRecord {
  id: string;
  company_id: string;
  user_id: string;
  name: string;
  specialty: string;
  credentials: string;
  cases_testified: number;
  win_rate: number;
  availability: string;
  availability_notes: string;
  contact_phone: string;
  contact_email: string;
  contact_location: string;
  bio: string;
  notable_cases: ExpertNotableCase[];
  publications: ExpertPublication[];
  rate_per_hour: number;
  cross_exam_notes: string;
  years_experience: number;
  board_certified: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExpertBookingRecord {
  id: string;
  expert_id: string;
  case_id: string;
  case_name?: string;
  start_date: string;
  end_date: string;
  notes: string;
  created_at: string;
}

// ExpertWitnessPage used to auto-seed THREE fabricated experts (invented
// names, phone numbers, emails, win rates) per case from a hash-of-case-id
// generator, alongside any real experts a user added. Removed entirely —
// the roster is now a real, firm-owned list attorneys build themselves.
export const expertsApi = {
  getAll: async (): Promise<{ experts: ExpertRecord[] }> => {
    if (isDemoMode()) return demoDelay({ experts: [] });
    const res = await fetchWithAuth('/experts');
    return firmJson(res, 'Failed to load experts');
  },
  create: async (data: Partial<Omit<ExpertRecord, 'id' | 'company_id' | 'user_id' | 'created_at' | 'updated_at'>> & { name: string }): Promise<{ expert: ExpertRecord }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const expert = {
        id: `expert-${Date.now()}`, company_id: 'demo-company', user_id: 'demo-user', specialty: 'Other',
        credentials: '', cases_testified: 0, win_rate: 0, availability: 'Available', availability_notes: '',
        contact_phone: '', contact_email: '', contact_location: '', bio: '', notable_cases: [], publications: [],
        rate_per_hour: 0, cross_exam_notes: '', years_experience: 0, board_certified: false,
        created_at: now, updated_at: now, ...data,
      } as ExpertRecord;
      return demoDelay({ expert });
    }
    const res = await fetchWithAuth('/experts', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to add expert');
  },
  update: async (id: string, data: Partial<Omit<ExpertRecord, 'id' | 'company_id' | 'user_id' | 'created_at' | 'updated_at'>>): Promise<{ expert: ExpertRecord }> => {
    if (isDemoMode()) return demoDelay({ expert: { id, ...data } as ExpertRecord });
    const res = await fetchWithAuth(`/experts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update expert');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/experts/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to delete expert');
  },
  getAllBookings: async (): Promise<{ bookings: ExpertBookingRecord[] }> => {
    if (isDemoMode()) return demoDelay({ bookings: [] });
    const res = await fetchWithAuth('/experts/bookings/all');
    return firmJson(res, 'Failed to load bookings');
  },
  createBooking: async (expertId: string, data: { case_id: string; start_date: string; end_date: string; notes?: string }): Promise<{ booking: ExpertBookingRecord }> => {
    if (isDemoMode()) {
      const booking = { id: `booking-${Date.now()}`, expert_id: expertId, created_at: new Date().toISOString(), notes: '', ...data } as ExpertBookingRecord;
      return demoDelay({ booking });
    }
    const res = await fetchWithAuth(`/experts/${expertId}/bookings`, { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to book expert');
  },
};

export interface AppealMilestoneRecord {
  id: string;
  appeal_id: string;
  case_id: string;
  type: string;
  status: 'Not Started' | 'In Progress' | 'Completed' | 'Overdue';
  due_date: string;
  completed_date: string | null;
  notes: string;
  documents: string[];
  created_at: string;
  updated_at: string;
}

// AppellateTrackerPage used to seed both appeals and their milestone
// checklists from hardcoded MOCK_APPEALS/MOCK_MILESTONES constants into
// component state — Track Appeal, toggling a milestone, editing notes/
// status/due date, and the document checklist all only ever touched local
// state.
export const appealsApi = {
  getAll: async (): Promise<{ appeals: AppealCaseRecord[] }> => {
    if (isDemoMode()) return demoDelay({ appeals: [] });
    const res = await fetchWithAuth('/appeals');
    return firmJson(res, 'Failed to load appeals');
  },
  create: async (data: {
    case_id: string; lower_court_judgment_date: string; appellant?: string; appellee?: string;
    court?: string; docket_number?: string; priority?: string; status?: string;
  }): Promise<{ appeal: AppealCaseRecord; milestones: AppealMilestoneRecord[] }> => {
    if (isDemoMode()) {
      const now = new Date().toISOString();
      const appeal = {
        id: `appeal-${Date.now()}`, user_id: 'demo-user', appellant: '', appellee: '', court: '',
        docket_number: 'TBD', priority: 'Medium', status: 'Active', created_at: now, updated_at: now, ...data,
      } as AppealCaseRecord;
      return demoDelay({ appeal, milestones: [] });
    }
    const res = await fetchWithAuth('/appeals', { method: 'POST', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to track appeal');
  },
  delete: async (id: string): Promise<{ success: boolean }> => {
    if (isDemoMode()) return demoDelay({ success: true });
    const res = await fetchWithAuth(`/appeals/${id}`, { method: 'DELETE' });
    return firmJson(res, 'Failed to remove appeal');
  },
  getAllMilestones: async (): Promise<{ milestones: AppealMilestoneRecord[] }> => {
    if (isDemoMode()) return demoDelay({ milestones: [] });
    const res = await fetchWithAuth('/appeal-milestones');
    return firmJson(res, 'Failed to load milestones');
  },
  updateMilestone: async (
    id: string,
    data: Partial<Pick<AppealMilestoneRecord, 'status' | 'due_date' | 'completed_date' | 'notes' | 'documents'>>
  ): Promise<{ milestone: AppealMilestoneRecord }> => {
    if (isDemoMode()) return demoDelay({ milestone: { id, ...data } as AppealMilestoneRecord });
    const res = await fetchWithAuth(`/appeal-milestones/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    return firmJson(res, 'Failed to update milestone');
  },
};

// Default export for legacy imports (e.g., InterviewPanel, usePersonas hook)
// Also provides axios-style get/post/put/delete helpers used by some hooks.
const api = {
  auth: authApi,
  templates: templatesApi,
  simulations: simulationsApi,
  analytics: analyticsApi,
  notifications: notificationsApi,
  cases: casesApi,
  personas: personasApi,
  documents: documentsApi,
  contradictions: contradictionsApi,
  witnesses: witnessesApi,
  deadlines: deadlinesApi,
  argumentVersions: argumentVersionsApi,
  feedback: feedbackApi,
  panelists: panelistsApi,
  evidence: evidenceApi,
  timeline: timelineApi,
  collaboration: collaborationApi,
  arguments: argumentRepositoryApi,

  get: async (url: string) => {
    if (isDemoMode()) {
      // Minimal demo stubs for legacy axios-style callers
      if (url.includes('/personas/archetypes')) {
        return { data: { archetypes: demo.demoPersonas.map((p) => p.archetype_tag) } };
      }
      return { data: {} };
    }
    const res = await fetchWithAuth(url);
    return { data: await res.json() };
  },

  post: async (url: string, body?: any) => {
    if (isDemoMode()) {
      if (url.includes('/personas/generate-random')) {
        const personas = demo.generateMorePersonas(body?.count || 6, demo.demoPersonas);
        return { data: { personas: enrichPersonas(personas) } };
      }
      if (url.includes('/personas/generate-archetype')) {
        const p = demo.demoPersonas.find((p) => p.archetype_tag.toLowerCase().includes(body?.archetype_key?.toLowerCase())) || demo.demoPersonas[0];
        return { data: { persona: enrichPersonas([p])[0] } };
      }
      if (url.includes('/personas/expand')) {
        const personas = demo.generateMorePersonas(body?.count || 3);
        return { data: { personas: enrichPersonas(personas) } };
      }
      if (url.includes('/simulations/') && url.includes('/interview')) {
        const persona = demo.demoPersonas.find((p) => p.id === body?.personaId) || demo.demoPersonas[0];
        return {
          data: {
            response: {
              response_text: `${persona.name} considers your question carefully. As a ${persona.archetype_tag.toLowerCase()}, I would need more concrete evidence before shifting my view.`,
              inner_thought: 'The question touches on my core skepticism about unsupported claims.',
              trait_invoked: persona.archetype_tag,
            },
            persona,
          },
        };
      }
      return { data: { success: true } };
    }
    const res = await fetchWithAuth(url, { method: 'POST', body: JSON.stringify(body) });
    return { data: await res.json() };
  },

  put: async (url: string, body?: any) => {
    if (isDemoMode()) return { data: { success: true } };
    const res = await fetchWithAuth(url, { method: 'PUT', body: JSON.stringify(body) });
    return { data: await res.json() };
  },

  delete: async (url: string) => {
    if (isDemoMode()) return { data: { success: true } };
    const res = await fetchWithAuth(url, { method: 'DELETE' });
    return { data: await res.json() };
  },
};
export default api;
